import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, Info, Plus, LockKeyhole } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/hooks/useTranslation";

const bountyTypes = [
  "Innovation Grant",
  "Pilot Project",
  "Research Partnership",
  "Commercial Deployment",
  "Cross-Border Initiative"
];

const quebecPartners = [
  "Eglantine Kuntz",
  "Quebec Delegation – Houston",
  "Hydro-Québec International",
  "Investissement Québec",
  "CQDM (Quebec Consortium)",
  "Lion Electric",
  "Boralex Energy",
  "Quebec Port Authority",
  "McGill University",
  "Université Laval",
  "École de technologie supérieure"
];

// Escrow condition examples tailored for Quebec-Houston collaboration
const escrowConditionExamples = [
  "Funds will be held in escrow until the solution is approved by the Quebec Delegation – Houston and Port of Houston representatives.",
  "50% of the grant will be held in escrow until the solution passes Quebec environmental compliance testing, and the remaining 50% until Texas GLO Innovation validation.",
  "Funds will remain in escrow for 60 days after initial solution delivery to allow for cross-border regulatory review and testing.",
  "Escrow will be maintained until the solution demonstrates successful integration with existing port infrastructure as verified by both Quebec and Texas technical teams.",
  "Grant will be held in escrow until the solution meets all specified sustainability requirements and receives confirmation from Quebec environmental authorities."
];

// Release condition examples
const releaseConditionExamples = [
  "Funds will be released upon successful demonstration of emission reduction targets and approval by the Quebec-Houston review committee.",
  "Release of funds will occur in three stages: 30% upon acceptance of the pilot design, 30% after successful port integration testing, and 40% upon environmental impact validation.",
  "Funds will be released after a 90-day pilot period with measurable carbon reduction results reported by independent Quebec and Texas auditors.",
  "The grant will be released once the solution has been verified to meet or exceed sustainability metrics outlined in the Quebec-Houston collaboration agreement.",
  "Release of the grant requires approval from Quebec Delegation – Houston, Port of Houston, and Texas GLO Innovation representatives."
];

const DemoCallForSolutionsQuebecBounty = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isBounty, setIsBounty] = useState<string | null>(null);
  const [selectedArea, setSelectedArea] = useState<string>(""); 
  const [isAreaExpanded, setIsAreaExpanded] = useState(false);
  const [selectedLead, setSelectedLead] = useState<string>("");
  const [includePhoneNumber, setIncludePhoneNumber] = useState(false);
  const [reviewerName, setReviewerName] = useState<string>("");
  const [collaborators, setCollaborators] = useState<string[]>([]);
  
  const expertiseAreas = [
    { id: "smart-maritime-logistics", name: "Smart Maritime Logistics" },
    { id: "electrified-ground-equipment", name: "Electrified Ground Equipment" },
    { id: "clean-battery-systems", name: "Clean Battery Systems" },
    { id: "renewable-energy-integration", name: "Renewable Energy Integration" },
    { id: "digital-carbon-tracking", name: "Digital Carbon Tracking" },
    { id: "cross-border-collaboration", name: "Cross-Border Collaboration" }
  ];
  
  const [bountyAmount, setBountyAmount] = useState("");
  const [bountyType, setBountyType] = useState("");
  
  // Escrow and release conditions state
  const [escrowConditions, setEscrowConditions] = useState("");
  const [selectedEscrowCondition, setSelectedEscrowCondition] = useState<string | null>(null);
  const [customEscrowCondition, setCustomEscrowCondition] = useState("");
  
  const [releaseConditions, setReleaseConditions] = useState("");
  const [selectedReleaseCondition, setSelectedReleaseCondition] = useState<string | null>(null);
  const [customReleaseCondition, setCustomReleaseCondition] = useState("");
  
  const [leadPercentage, setLeadPercentage] = useState("40");
  const [collaboratorPercentage, setCollaboratorPercentage] = useState("35");
  const [reviewerPercentage, setReviewerPercentage] = useState("15");
  const [coordinatorPercentage, setCoordinatorPercentage] = useState("10");

  const [leadAssignment, setLeadAssignment] = useState<"open" | "assign">("open");
  const [collaboratorAssignment, setCollaboratorAssignment] = useState<"open" | "assign">("open");
  const [reviewerAssignment, setReviewerAssignment] = useState<"open" | "assign">("open");
  const [coordinatorAssignment, setCoordinatorAssignment] = useState<"open" | "assign">("open");
  
  const [leadPartner, setLeadPartner] = useState("");
  const [collaboratorPartner, setCollaboratorPartner] = useState("");
  const [reviewerPartner, setReviewerPartner] = useState("");
  const [coordinatorPartner, setCoordinatorPartner] = useState("");

  useEffect(() => {
    if (location.state && location.state.selectedArea) {
      const area = location.state.selectedArea;
      setSelectedArea(area);
      
      const areaName = expertiseAreas.find(a => a.id === area)?.name || area;
      setSelectedArea(areaName);
    }
  }, [location]);

  const handleBack = () => {
    navigate("/demo-call-for-solutions-quebec/functional-area");
  };

  const handleAddCollaborator = () => {
    setCollaborators([...collaborators, ""]);
  };

  const handleCollaboratorChange = (index: number, value: string) => {
    const updatedCollaborators = [...collaborators];
    updatedCollaborators[index] = value;
    setCollaborators(updatedCollaborators);
  };

  // Handle escrow condition selection
  useEffect(() => {
    if (selectedEscrowCondition === "other") {
      setEscrowConditions(customEscrowCondition);
    } else if (selectedEscrowCondition !== null) {
      setEscrowConditions(selectedEscrowCondition);
    }
  }, [selectedEscrowCondition, customEscrowCondition]);

  // Handle release condition selection
  useEffect(() => {
    if (selectedReleaseCondition === "other") {
      setReleaseConditions(customReleaseCondition);
    } else if (selectedReleaseCondition !== null) {
      setReleaseConditions(selectedReleaseCondition);
    }
  }, [selectedReleaseCondition, customReleaseCondition]);

  useEffect(() => {
    if (isBounty === "yes") {
      setBountyAmount("$75,000 CAD");
      setBountyType("Pilot Project");
      
      // Set default escrow and release conditions
      const defaultEscrowCondition = escrowConditionExamples[0];
      setSelectedEscrowCondition(defaultEscrowCondition);
      setEscrowConditions(defaultEscrowCondition);
      
      const defaultReleaseCondition = releaseConditionExamples[0];
      setSelectedReleaseCondition(defaultReleaseCondition);
      setReleaseConditions(defaultReleaseCondition);
      
      setLeadAssignment("assign");
      setLeadPartner("Quebec Delegation – Houston");
      setLeadPercentage("40");
      
      setCollaboratorAssignment("assign");
      setCollaboratorPartner("Lion Electric");
      setCollaboratorPercentage("35");
      
      setReviewerAssignment("assign");
      setReviewerPartner("McGill University");
      setReviewerPercentage("15");
      
      setCoordinatorAssignment("assign");
      setCoordinatorPartner("Eglantine Kuntz");
      setCoordinatorPercentage("10");
    }
  }, [isBounty]);

  const handleContinue = () => {
    if (isBounty === "yes") {
      navigate("/demo-call-for-solutions-quebec/bounty-details", { 
        state: { 
          selectedArea, 
          isBounty,
          bountyDetails: {
            bountyAmount,
            bountyType,
            escrowConditions,
            releaseConditions,
            attribution: {
              leadPercentage,
              collaboratorPercentage,
              reviewerPercentage,
              coordinatorPercentage
            },
            roleAssignments: {
              lead: {
                status: leadAssignment,
                assignedTo: leadAssignment === "assign" ? leadPartner : "Open"
              },
              collaborator: {
                status: collaboratorAssignment,
                assignedTo: collaboratorAssignment === "assign" ? collaboratorPartner : "Open"
              },
              reviewer: {
                status: reviewerAssignment,
                assignedTo: reviewerAssignment === "assign" ? reviewerPartner : "Open"
              },
              coordinator: {
                status: coordinatorAssignment,
                assignedTo: coordinatorAssignment === "assign" ? coordinatorPartner : "Open"
              }
            }
          }
        } 
      });
    } else {
      navigate("/demo-call-for-solutions-quebec/coordinator", { 
        state: { 
          selectedArea, 
          isBounty,
          selectedLead,
          teamAssignment: {
            reviewer: reviewerName,
            collaborators
          }
        } 
      });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/quebec-channel")}
        />
      </div>

      <div className="w-full max-w-2xl space-y-6">
        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsAreaExpanded(!isAreaExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">{t('quebec.bounty.selectExpertiseArea')}</h2>
              <p className="text-gray-600 mt-1">{t('quebec.bounty.selected')}: {selectedArea}</p>
            </div>
            <div>
              {isAreaExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">{t('quebec.bounty.solutionBounty')}</h2>
            <p className="text-gray-600 mb-4">{t('quebec.bounty.isInnovationGrant')}</p>
            
            <RadioGroup 
              value={isBounty || ""} 
              onValueChange={(value) => {
                setIsBounty(value);
                if (value === "yes") {
                  setSelectedLead("");
                }
              }}
              className="flex space-x-8"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="yes" />
                <Label htmlFor="yes">{t('quebec.bounty.yes')}</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="no" />
                <Label htmlFor="no">{t('quebec.bounty.no')}</Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {isBounty === "yes" && (
          <Card className="border border-gray-200">
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">{t('quebec.bounty.grantAmount')}</Label>
                    <Input 
                      value={bountyAmount} 
                      onChange={(e) => setBountyAmount(e.target.value)}
                      placeholder={t('quebec.bounty.enterAmountCAD')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">{t('quebec.bounty.grantType')}</Label>
                    <Select value={bountyType} onValueChange={setBountyType}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t('quebec.bounty.selectType')} />
                      </SelectTrigger>
                      <SelectContent>
                        {bountyTypes.map(type => (
                          <SelectItem key={type} value={type.toLowerCase()}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between mt-8">
          <Button 
            variant="outline" 
            onClick={handleBack}
            className="border-gray-300"
          >
            {t('ui.back')}
          </Button>
          <Button 
            onClick={handleContinue}
            disabled={!isBounty}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
          >
            {t('ui.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoCallForSolutionsQuebecBounty;